import React, { useEffect, useState, useRef } from 'react';
import PageHeader from './components/PageHeader';
import Pagination from './components/Pagination';
import IconButton from './components/IconButton';
import EditActionIcon from './components/EditActionIcon';
import SortableTableHeader from './components/SortableTableHeader';
import { LabelPrintsIcon } from './Sidebar';
import { showToastError, showToastSuccess } from './components/ToastNotifications';
import { getFacilitiesByOrganization } from './hooks/useFacilities';
import { getAllMarkets } from './hooks/useMarkets';
import { getPackagesStagedTotals, getPackagesTotals, syncPackageCoaMultiple } from './hooks/usePackages';
import { useUrlFilters, useUrlParam } from './hooks/urlParams';
import type { PackageRecord, PagedResult, StagedPackageRecord } from './types/index';
import { UNITS_OF_MEASURE } from './constants/unitsOfMeasure';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from './constants';
import { getNextSortCriterion, type FieldSortCriterionValue } from './utils/tableSort';
import { invalidateListRequest } from './utils/requestVersion';
import { ssoErrorFromItemResult } from './utils/requestErrors';
import { useSsoReauth } from './contexts/SsoReauthContext';

type PackagePageResult<T> = PagedResult<T> & {
  pagePending: number;
};

const formatDate = (value: string | Date | null | undefined) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const descendingFirstFields = new Set(['importedAt', 'lastModified']);
const PACKAGE_TABS = ['Active', 'Staged'];
const DEFAULT_PACKAGE_TAB = PACKAGE_TABS[0];
const uomAbbr = (name?: string | null): string => {
  if (!name) return '';
  const match = UNITS_OF_MEASURE.find((unit) => unit.Name.toLowerCase() === name.toLowerCase());
  return match?.Abbreviation ?? name;
};

export default function PackagesPage({
  organizationId,
  onAdd: _onAdd,
  onStage,
  onImport,
  onEdit,
  onEditStaged,
  onPrint,
  onPrintStaged,
  currentUser,
  stagedRefreshKey = 0,
}) {
  const { handleSsoError } = useSsoReauth();

  const activePackagesRequestIdRef = useRef(0);
  const stagedPackagesRequestIdRef = useRef(0);

  const [filters, setFilters] = useUrlFilters<{
    facilityId: string;
    marketId: string;
    searchTerm: string;
    tab: string;
  }>({
    facilityId: { default: 'all', type: 'string' },
    marketId: { default: 'all', type: 'string' },
    searchTerm: { default: '', type: 'string' },
    tab: {
      default: DEFAULT_PACKAGE_TAB,
      type: 'string',
      validate: (value) => (PACKAGE_TABS.includes(value as string) ? value : undefined),
    },
  });
  const [activeLoading, setActiveLoading] = useState(false);
  const [stagedLoading, setStagedLoading] = useState(false);
  const [relinking, setRelinking] = useState(false);
  const [selectedPackageIds, setSelectedPackageIds] = useState<Set<string>>(new Set());
  const [activeRefreshKey, setActiveRefreshKey] = useState(0);
  const activeTab = filters.tab;
  const setActiveTab = (tab: string) => {
    setFilters((current) => ({ ...current, tab }));
  };
  const [pageParam, setPageParam] = useUrlParam<number>('page', 0);
  const [pageSizeParam, setPageSizeParam] = useUrlParam<number>('pageSize', DEFAULT_PAGE_SIZE, {
    validate: (value) => PAGE_SIZE_OPTIONS.includes(value as (typeof PAGE_SIZE_OPTIONS)[number])
      ? value
      : undefined,
  });

  const [results, setResults] = useState<PackagePageResult<PackageRecord>>({
    page: pageParam,
    pagePending: pageParam,
    pageSize: pageSizeParam,
    total: 0,
    data: []
  });

  const [activeSort, setActiveSort] = useState<FieldSortCriterionValue>({
    fieldName: 'importedAt',
    isAscending: false
  });

  const [stagedSort, setStagedSort] = useState<FieldSortCriterionValue>({
    fieldName: 'lastModified',
    isAscending: false,
  });

  const [stagedPackages, setStagedPackages] = useState<PackagePageResult<StagedPackageRecord>>({
    page: 0,
    pagePending: 0,
    pageSize: pageSizeParam,
    total: 0,
    data: []
  });

  const [facilityOptions, setFacilityOptions] = useState({
    page: 0,
    pageSize: 500,
    total: 0,
    data: []
  });

  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    setResults((prev) =>
      prev.page !== pageParam ? { ...prev, page: pageParam, pagePending: pageParam } : prev,
    );
  }, [pageParam]);

  useEffect(() => {
    setPageParam(results.page);
  }, [results.page, setPageParam]);

  useEffect(() => {
    setResults((prev) => prev.pageSize !== pageSizeParam
      ? { ...prev, pageSize: pageSizeParam, page: 0, pagePending: 0 }
      : prev);
    setStagedPackages((prev) => prev.pageSize !== pageSizeParam
      ? { ...prev, pageSize: pageSizeParam, page: 0, pagePending: 0 }
      : prev);
  }, [pageSizeParam]);

  useEffect(() => {
    const requestId = ++activePackagesRequestIdRef.current;
    async function loadPackages() {
      try {
        const { pagePending: requestedPage, pageSize } = results;
        const { facilityId, marketId, searchTerm } = filters;
        const payload: Record<string, any> = {
            organizationId,
            page: requestedPage,
            pageSize
        };

        if (facilityId && facilityId !== 'all') {
          payload['facilityId'] = facilityId;
        }

        if (marketId && marketId !== 'all') {
          payload['marketId'] = marketId;
        }

        if (searchTerm) {
          payload['label'] = searchTerm;
        }
        payload['importedAtSortAscending'] = activeSort.fieldName === 'importedAt'
          ? activeSort.isAscending
          : false;
        payload['sortField'] = activeSort.fieldName;
        payload['sortAscending'] = activeSort.isAscending;
        setActiveLoading(true);
        const response = await getPackagesTotals(payload);
        if (requestId !== activePackagesRequestIdRef.current) return;
        setActiveLoading(false);
        for (const pkg of response.data) {
            if (pkg.importedAt) {
                pkg.importedAt = new Date(pkg.importedAt);
            }
            if (pkg.lastModified) {
                pkg.lastModified = new Date(pkg.lastModified);
            }
        }
        setResults({
          ...response,
          page: requestedPage,
          pagePending: requestedPage,
        });
      } catch {
        if (requestId !== activePackagesRequestIdRef.current) return;
        setActiveLoading(false);
        showToastError('Packages could not be loaded. Please refresh the page or try again.');
      }
    }
    if (organizationId)
      loadPackages();

  // Depend on the request-shaping filter fields only; `filters.tab` changes the view, not the query.
  }, [activeRefreshKey, activeSort.fieldName, activeSort.isAscending, filters.facilityId, filters.marketId, filters.searchTerm, organizationId, results.pagePending, results.pageSize]);
  
  useEffect(() => {
    const requestId = ++stagedPackagesRequestIdRef.current;
    async function loadStagedPackages() {
      try {
        const { pagePending: requestedPage, pageSize } = stagedPackages;
        const { facilityId, marketId, searchTerm } = filters;
          const payload: Record<string, any> = {
              organizationId,
              page: requestedPage,
              pageSize
          };

        if (facilityId && facilityId !== 'all') {
          payload['facilityId'] = facilityId;
        }

        if (marketId && marketId !== 'all') {
          payload['marketId'] = marketId;
        }

        if (searchTerm) {
          payload['label'] = searchTerm;
        }
        payload['sortField'] = stagedSort.fieldName;
        payload['sortAscending'] = stagedSort.isAscending;

        setStagedLoading(true);
        const packagesStagedResponse = await getPackagesStagedTotals(payload);
        if (requestId !== stagedPackagesRequestIdRef.current) return;
        setStagedLoading(false);

        for (const pkg of packagesStagedResponse.data) {
            if (pkg.lastModified) {
                pkg.lastModified = new Date(pkg.lastModified);
            }
        }
        setStagedPackages(prev => ({
            ...prev,
            data: packagesStagedResponse.data,
            total: packagesStagedResponse.total,
            page: requestedPage,
            pagePending: requestedPage,
        }));
      } catch {
        if (requestId !== stagedPackagesRequestIdRef.current) return;
        setStagedLoading(false);
        showToastError('Staged packages could not be loaded. Please refresh the page or try again.');
      }
    }
    if (organizationId)
      loadStagedPackages();

  }, [
    filters.facilityId,
    filters.marketId,
    filters.searchTerm,
    organizationId,
    stagedPackages.pagePending,
    stagedPackages.pageSize,
    stagedSort.fieldName,
    stagedSort.isAscending,
    stagedRefreshKey,
  ]);

  useEffect(() => {
    async function getFacilities() {
      try {
        const response = await getFacilitiesByOrganization(organizationId);
        setFacilityOptions(response);
      } catch (e) {
        console.log(e);
      }
    }
    if (organizationId) {
      getFacilities();
    }
  }, [organizationId]);

  useEffect(() => {

    async function getMarkets() {
      try {
        const markets = await getAllMarkets(organizationId);
        const marketsSorted = markets?.sort((a, b) => a.name > b.name ? 1 : -1);
        setMarkets(marketsSorted);
        setMarkets(markets);
      } catch (e) {
        console.log(e);
      }
    }

    if (organizationId) {
      getMarkets();
    }

  }, [organizationId]);

  const getMarketName = (facilityId) => {
    const facility = facilityOptions.data.find(f => f.id == facilityId);
    if (!facility) return '—';
    const market = markets.find(m => m.id == facility.marketId);
    return market ? market.name : '—';
  }

  const handleActiveSortChange = (fieldName: string) => {
    invalidateListRequest(activePackagesRequestIdRef);
    setActiveSort((current) => getNextSortCriterion(current, fieldName, descendingFirstFields));
    setResults((current) => ({ ...current, page: 0, pagePending: 0, total: 0, data: [] }));
  };

  const handleStagedSortChange = (fieldName: string) => {
    invalidateListRequest(stagedPackagesRequestIdRef);
    setStagedSort((current) => getNextSortCriterion(current, fieldName, descendingFirstFields));
    setStagedPackages((current) => ({ ...current, page: 0, pagePending: 0, total: 0, data: [] }));
  };

  const applyFilters = (updater) => {
    invalidateListRequest(activePackagesRequestIdRef);
    invalidateListRequest(stagedPackagesRequestIdRef);
    setActiveLoading(true);
    setStagedLoading(true);
    setResults((prev) => ({ ...prev, page: 0, pagePending: 0, total: 0, data: [] }));
    setStagedPackages((prev) => ({ ...prev, page: 0, pagePending: 0, total: 0, data: [] }));
    setFilters(updater);
  };

  useEffect(() => {
    setSelectedPackageIds(new Set());
  }, [activeTab, filters.facilityId, filters.marketId, filters.searchTerm, organizationId, results.page, results.pagePending, results.pageSize]);

  const selectablePackages = (results.data || []).filter((pkg) => pkg.id !== null && pkg.id !== undefined);
  const selectedPackages = selectablePackages.filter((pkg) => selectedPackageIds.has(String(pkg.id)));
  const allPackagesSelected = selectablePackages.length > 0 && selectedPackages.length === selectablePackages.length;

  const togglePackageSelection = (packageId: string | number, selected: boolean) => {
    setSelectedPackageIds((current) => {
      const next = new Set(current);
      if (selected) {
        next.add(String(packageId));
      } else {
        next.delete(String(packageId));
      }
      return next;
    });
  };

  const handleRelink = async () => {
    if (!organizationId || selectedPackages.length === 0) return;

    setRelinking(true);
    try {
      const relinks = await syncPackageCoaMultiple(organizationId, selectedPackages.map((pkg) => pkg.id));
      const allFailures = Array.isArray(relinks)
        ? relinks.filter((relink) => relink?.success === false)
        : [];
      const successCount = selectedPackages.length - allFailures.length;
      // Packages that failed only because no Metrc sign-in works share one sign-in prompt.
      const ssoError = allFailures.map(ssoErrorFromItemResult).find(Boolean);
      const failures = ssoError && handleSsoError(ssoError)
        ? allFailures.filter((failure) => !ssoErrorFromItemResult(failure))
        : allFailures;

      if (successCount > 0) {
        showToastSuccess(`${successCount} package${successCount === 1 ? '' : 's'} successfully relinked.`);
      }
      if (failures.length > 0) {
        showToastError(failures.map((failure: { packageId: number; error?: string }) =>
          `Package ${failure.packageId}: ${failure.error || 'Unable to relink.'}`).join('\n'));
      }
    } catch {
      showToastError(`Unable to relink ${selectedPackages.length} package${selectedPackages.length === 1 ? '' : 's'}.`);
    } finally {
      setSelectedPackageIds(new Set());
      setRelinking(false);
      setActiveRefreshKey((current) => current + 1);
    }
  };

  const normalizedPackageSearch = String(filters?.searchTerm || '').trim().toLowerCase();
  const visibleStagedPackages = (Array.isArray(stagedPackages?.data) ? stagedPackages.data : [])
    .filter((pkg) => {
      if (!normalizedPackageSearch) return true;
      return String(pkg?.packageLabel || '').toLowerCase().includes(normalizedPackageSearch);
    });
  const stagedRowsKey = [
    normalizedPackageSearch,
    ...visibleStagedPackages.map((pkg, index) => String(pkg?.tagId ?? pkg?.id ?? pkg?.packageLabel ?? index)),
  ].join('|');
  const canWritePackages = currentUser?.roleActions?.includes('WritePackages') ||
    currentUser?.roleActions?.includes('AdminPackages');
  const canWriteImports = currentUser?.roleActions?.includes('WriteImports') ||
    currentUser?.roleActions?.includes('AdminImports');

  return (
    <div className="stack" data-testid="page-packages">
      <PageHeader
        title="Packages"
        subtitle="Package Tracking"
      />

      <div className="card filter-card" style={{ marginBottom: 12 }}>
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              placeholder="Search by package label"
              value={filters.searchTerm}
              onChange={(e) => {
                applyFilters((current) => ({ ...current, searchTerm: e.target.value }));
              }}
              data-testid="packages-search"
            />
            <select
              className="select"
              value={filters.facilityId}
              onChange={(e) => applyFilters((current) => ({ ...current, facilityId: e.target.value }))}
              style={{ minWidth: 200 }}
              data-testid="packages-filter-facility"
            >
              <option value="all">All facility licenses</option>
              {[...facilityOptions.data]
                .sort((a, b) => (a.name || '').localeCompare((b.name || ''), undefined, { sensitivity: 'base' }))
                .map((f) => (
                <option key={f.id} value={f.id} data-testid={`packages-filter-facility-option-${f.id}`}>{f.name}</option>
              ))}
            </select>
            <select
              className="select"
              value={filters.marketId ?? 'all'}
              onChange={(e) => {
                  applyFilters((current) => ({ ...current, marketId: e.target.value }));
              }}
              data-testid="packages-filter-market"
            >
              <option value="all">All Markets</option>
              {(markets || [])
                .slice()
                .sort((a, b) => (a.name || a.code || '').localeCompare((b.name || b.code || ''), undefined, { sensitivity: 'base' }))
                .map((m) => (
                  <option key={String(m.id)} value={String(m.id)}>{m.name || m.code}</option>
              ))}
            </select>
          </div>
          {canWritePackages &&
          <div className="filter-right">
            {canWriteImports && <>
              <button className="button cta" onClick={onImport} data-testid="packages-import">
                <span className="plus" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Import
              </button>
              <button className="button cta" onClick={onStage} data-testid="packages-stage">
                <span className="plus" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                Stage
              </button>
            </>}
            <button
              className="button cta"
              onClick={handleRelink}
              disabled={selectedPackages.length === 0 || relinking}
              data-testid="packages-relink"
            >
              Relink
            </button>
          </div>}
        </div>
      </div>
      <div className="drawer-tabs" role="tablist" aria-label="Packages tabs">
          <button
              type="button"
              className={`drawer-tab ${activeTab === 'Active' ? 'active' : ''}`}
              onClick={() => setActiveTab('Active')}
              role="tab"
                  aria-selected={activeTab === 'Active'}
                data-testid="packages-tab-active"
          >
              Active
          </button>
          <button
              type="button"
              className={`drawer-tab ${activeTab === 'Staged' ? 'active' : ''}`}
              onClick={() => setActiveTab('Staged')}
              role="tab"
              aria-selected={activeTab === 'Staged'}
              data-testid="packages-tab-staged"
          >
              Staged
          </button>
      </div>
      <div className="card">
        {activeTab === 'Active' ?
            (<table className="table" data-testid="packages-active-table">
                <thead>
                    <tr>
                    {canWritePackages && <th style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              aria-label="Select all packages"
                              checked={allPackagesSelected}
                              disabled={selectablePackages.length === 0 || relinking}
                              onChange={(event) => {
                                setSelectedPackageIds(event.target.checked
                                  ? new Set(selectablePackages.map((pkg) => String(pkg.id)))
                                  : new Set());
                              }}
                              data-testid="packages-active-select-all"
                            />
                        </th>}
                        <SortableTableHeader label="Tag" fieldName="label" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Product" fieldName="productName" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Strain" fieldName="strain" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Category" fieldName="productCategoryName" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Package Quantity" fieldName="quantity" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Label Quantity" fieldName="labelQuantity" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Status" fieldName="labTestingStateName" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Market" fieldName="marketName" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Facility License" fieldName="facilityLicense" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Imported" fieldName="importedAt" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Imported Source" fieldName="importedSource" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <SortableTableHeader label="Last Updated" fieldName="lastModified" sort={activeSort} onSort={handleActiveSortChange} align="center" />
                        <th aria-label="actions" />
                    </tr>
                </thead>
                <tbody>
                    {(results.data || []).map((pkg, index) => {
                      const packageRowId = pkg.id ?? `${pkg.label || 'missing'}-${index}`;
                      return (
                        <tr key={packageRowId} data-testid={`packages-active-row-${packageRowId}`}>
                      {canWritePackages && <td style={{ textAlign: 'center' }}>
                            {pkg.id !== null && pkg.id !== undefined && (
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${pkg.label || pkg.id}`}
                                  checked={selectedPackageIds.has(String(pkg.id))}
                                  disabled={relinking}
                                  onChange={(event) => togglePackageSelection(pkg.id, event.target.checked)}
                                  data-testid={`packages-active-select-${pkg.id}`}
                                />
                            )}
                            </td>}
                            <td style={{ textAlign: 'center' }}>{pkg.label}</td>
                            <td style={{ textAlign: 'center' }}>{pkg.productName}</td>
                            <td style={{ textAlign: 'center' }}>{pkg.strain}</td>
                            <td style={{ textAlign: 'center' }}>{pkg.productCategoryName}</td>
                            <td style={{ textAlign: 'center' }}>
                              {pkg.quantity ?? '0'}
                              {pkg.unitOfMeasureName && (
                                <span style={{ fontSize: '0.75rem', marginLeft: '0.25em' }}>
                                  {uomAbbr(pkg.unitOfMeasureName)}
                                </span>
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>{pkg.labelQuantity ?? 0}</td>
                            <td style={{ textAlign: 'center' }}>{pkg.labTestingStateName}</td>
                            <td style={{ textAlign: 'center' }}>{pkg.marketName || getMarketName(pkg.facilityId)}</td>
                            <td style={{ textAlign: 'center' }}>{pkg.facilityLicense?.trim() || (pkg.facilityId != null ? facilityOptions.data?.find(fo => fo?.id != null && String(fo.id) === String(pkg.facilityId))?.license?.trim() : '') || '—'}</td>
                            <td style={{ textAlign: 'center' }}>{formatDate(pkg.importedAt)}</td>
                            <td style={{ textAlign: 'center' }}>{pkg.importedSource || ''}</td>
                            <td style={{ textAlign: 'center' }}>{formatDate(pkg.lastModified)}</td>
                            <td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
                                {pkg.hasBackingPackage !== false &&
                                 (currentUser?.roleActions?.indexOf('WritePackages') >= 0 ||
                                  currentUser?.roleActions?.indexOf('AdminPackages') >= 0) && <IconButton title="Edit" ariaLabel={`Edit ${pkg.id || 'package'}`} onClick={() => onEdit && onEdit(pkg)} testId={`packages-active-edit-${pkg.id}`}>
                                    <EditActionIcon />
                                </IconButton>}
                                {pkg.hasBackingPackage !== false &&
                                 (currentUser?.roleActions?.indexOf('WritePrintRuns') >= 0 ||
                                  currentUser?.roleActions?.indexOf('AdminPrintRuns') >= 0) && <IconButton title="Print" ariaLabel={`Print ${pkg.label || 'package'}`} onClick={() => onPrint && onPrint(pkg)} testId={`packages-active-print-${pkg.id}`}>
                                    <LabelPrintsIcon />
                                </IconButton>}
                            </td>
                        </tr>
                      );
                    })}
                    {(results.data || []).length === 0 && (
                      <tr data-testid={activeLoading ? 'packages-active-loading' : 'packages-active-empty'}>
                        <td colSpan={canWritePackages ? 14 : 13}>{activeLoading ? 'Searching for packages...' : 'No packages found.'}</td>
                      </tr>
                    )}
                </tbody>
            </table>) : (<template></template>)}

          {activeTab === 'Staged' ? (<table className="table" data-testid="packages-staged-table">
          <thead>
            <tr>
              <SortableTableHeader label="Tag" fieldName="packageLabel" sort={stagedSort} onSort={handleStagedSortChange} align="center" />
              <SortableTableHeader label="Product" fieldName="productName" sort={stagedSort} onSort={handleStagedSortChange} align="center" />
              <SortableTableHeader label="Label Quantity" fieldName="labelQuantity" sort={stagedSort} onSort={handleStagedSortChange} align="center" />
              <SortableTableHeader label="Market" fieldName="marketName" sort={stagedSort} onSort={handleStagedSortChange} align="center" />
              <SortableTableHeader label="Facility" fieldName="facilityName" sort={stagedSort} onSort={handleStagedSortChange} align="center" />
              <SortableTableHeader label="Last Updated" fieldName="lastModified" sort={stagedSort} onSort={handleStagedSortChange} align="center" />
              <th aria-label="actions" />
            </tr>
          </thead>
          <tbody key={stagedRowsKey}>
            {visibleStagedPackages.map((pkg, index) => {
              const stagedPackageId = pkg?.tagId ?? pkg?.id ?? `${pkg?.packageLabel ?? 'missing'}-${index}`;
              return (
              <tr key={stagedPackageId} data-testid={`packages-staged-row-${stagedPackageId}`}>
                <td style={{ textAlign: 'center' }}>{pkg.packageLabel}</td>
                <td style={{ textAlign: 'center' }}>{pkg.productName}</td>
                <td style={{ textAlign: 'center' }}>{pkg.labelQuantity ?? 0}</td>
                <td style={{ textAlign: 'center' }}>{pkg.marketName || getMarketName(pkg.facilityId)}</td>
                <td style={{ textAlign: 'center' }}>{pkg.facilityName || facilityOptions.data.find(fo => fo.id == pkg.facilityId)?.name || '—'}</td>
                <td style={{ textAlign: 'center' }}>{formatDate(pkg.lastModified)}</td>
                <td style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
                        {!pkg.sourcePackageId && onEditStaged &&
                            (currentUser?.roleActions?.indexOf('WritePackages') >= 0 ||
                            currentUser?.roleActions?.indexOf('AdminPackages') >= 0) && (
                      <IconButton title="Edit" ariaLabel={`Edit ${pkg.packageLabel || 'package'}`} onClick={() => onEditStaged(pkg)} testId={`packages-staged-edit-${stagedPackageId}`}>
                        <EditActionIcon />
                      </IconButton>
                    )}
                    {(currentUser?.roleActions?.indexOf('WritePrintRuns') >= 0 ||
                      currentUser?.roleActions?.indexOf('AdminPrintRuns') >= 0) && <IconButton title="Print" ariaLabel={`Print ${pkg.label || 'package'}`} onClick={() => onPrintStaged && onPrintStaged(pkg)} testId={`packages-staged-print-${stagedPackageId}`}>
                        <LabelPrintsIcon />
                    </IconButton>}
                </td>
              </tr>
              );
            })}
            {visibleStagedPackages.length === 0 && (
              <tr data-testid={stagedLoading ? 'packages-staged-loading' : 'packages-staged-empty'}>
                <td colSpan={7}>{stagedLoading ? 'Searching for staged packages...' : 'No staged packages found.'}</td>
              </tr>
            )}
          </tbody>
        </table>) : (<template></template>)}

        {activeTab === 'Active' ? (
            <Pagination
              page={results.page}
              totalPages={Math.ceil(results.total / results.pageSize)}
              onPrev={() => { setResults((prev) => ({ ...prev, pagePending: prev.page - 1 })) }}
              onNext={() => { setResults((prev) => ({ ...prev, pagePending: prev.page + 1 })) }}
              format="of"
              prevLabel="◀"
              nextLabel="▶"
              prevDisabled={activeLoading}
              nextDisabled={activeLoading}
              pageSize={results.pageSize}
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              testId="packages-active-pagination"
              onPageSizeChange={(size) => {
                setPageSizeParam(size);
                setResults((prev) => ({ ...prev, pageSize: size, page: 0, pagePending: 0 }));
                setStagedPackages((prev) => ({ ...prev, pageSize: size, page: 0, pagePending: 0 }));
              }}
                  />
              ) : (
            <Pagination
                page={stagedPackages.page}
                totalPages={Math.ceil(stagedPackages.total / stagedPackages.pageSize)}
                onPrev={() => { setStagedPackages((prev) => ({ ...prev, pagePending: prev.page - 1 })) }}
                onNext={() => { setStagedPackages((prev) => ({ ...prev, pagePending: prev.page + 1 })) }}
                format="of"
                prevLabel="◀"
                nextLabel="▶"
                prevDisabled={stagedLoading}
                nextDisabled={stagedLoading}
                pageSize={stagedPackages.pageSize}
                pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
                testId="packages-staged-pagination"
                onPageSizeChange={(size) => {
                  setPageSizeParam(size);
                  setResults((prev) => ({ ...prev, pageSize: size, page: 0, pagePending: 0 }));
                  setStagedPackages((prev) => ({ ...prev, pageSize: size, page: 0, pagePending: 0 }));
                }}
            />
        )}

      </div>
    </div>
  );
}
